import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { FaQuestion, FaReply, FaClock, FaCheck } from 'react-icons/fa';
import { useAuth } from '../contexts/AuthContext';
import { apiRequest } from '../utils/api';

const QuestionsContainer = styled.div`
  padding: 20px 0;
`;

const AskQuestionButton = styled.button`
  background: #28a745;
  color: white;
  border: none;
  padding: 12px 24px;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.3s;
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 30px;

  &:hover {
    background: #218838;
  }

  &:disabled {
    background: #6c757d;
    cursor: not-allowed;
  }
`;

const QuestionForm = styled.div`
  background: white;
  border: 1px solid #e9ecef;
  border-radius: 12px;
  padding: 24px;
  margin-bottom: 30px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
`;

const FormTitle = styled.h3`
  margin: 0 0 16px 0;
  color: #333;
  font-size: 18px;
  font-weight: 600;
`;

const QuestionTextarea = styled.textarea`
  width: 100%;
  min-height: 120px;
  padding: 12px;
  border: 1px solid #ddd;
  border-radius: 8px;
  font-size: 14px;
  font-family: inherit;
  resize: vertical;
  margin-bottom: 16px;

  &:focus {
    outline: none;
    border-color: #ff6b35;
    box-shadow: 0 0 0 2px rgba(255, 107, 53, 0.2);
  }
`;

const FormActions = styled.div`
  display: flex;
  gap: 12px;
  justify-content: flex-end;
`;

const CancelButton = styled.button`
  background: #6c757d;
  color: white;
  border: none;
  padding: 8px 16px;
  border-radius: 6px;
  font-size: 14px;
  cursor: pointer;
  transition: background-color 0.3s;

  &:hover {
    background: #5a6268;
  }
`;

const SubmitButton = styled.button`
  background: #ff6b35;
  color: white;
  border: none;
  padding: 8px 16px;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.3s;

  &:hover {
    background: #e55a2b;
  }

  &:disabled {
    background: #ccc;
    cursor: not-allowed;
  }
`;

const QuestionsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const QuestionItem = styled.div`
  background: white;
  border: 1px solid #e9ecef;
  border-radius: 12px;
  padding: 20px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
`;

const QuestionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 12px;
`;

const QuestionInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex: 1;
`;

const UserAvatar = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: #ff6b35;
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  font-size: 14px;
`;

const QuestionMeta = styled.div`
  flex: 1;
`;

const QuestionAuthor = styled.div`
  font-weight: 600;
  color: #333;
  font-size: 14px;
  margin-bottom: 4px;
`;

const QuestionDate = styled.div`
  color: #666;
  font-size: 12px;
  display: flex;
  align-items: center;
  gap: 4px;
`;

const QuestionActions = styled.div`
  display: flex;
  gap: 8px;
`;

const ActionButton = styled.button`
  background: none;
  border: none;
  padding: 6px;
  border-radius: 4px;
  cursor: pointer;
  color: #666;
  transition: all 0.3s;
  font-size: 14px;

  &:hover {
    background: #f8f9fa;
    color: #ff6b35;
  }

  &.delete:hover {
    color: #dc3545;
  }
`;

const QuestionText = styled.div`
  color: #333;
  font-size: 14px;
  line-height: 1.5;
  margin-bottom: 16px;
`;

const AnswerSection = styled.div`
  background: #f8f9fa;
  border-left: 4px solid #ff6b35;
  padding: 16px;
  border-radius: 0 8px 8px 0;
  margin-top: 12px;
`;

const AnswerHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
  font-size: 12px;
  color: #666;
`;

const AnswerText = styled.div`
  color: #333;
  font-size: 14px;
  line-height: 1.5;
`;

const AnswerForm = styled.div`
  background: #f8f9fa;
  border: 1px solid #e9ecef;
  border-radius: 8px;
  padding: 16px;
  margin-top: 12px;
`;

const AnswerTextarea = styled.textarea`
  width: 100%;
  min-height: 80px;
  padding: 8px;
  border: 1px solid #ddd;
  border-radius: 6px;
  font-size: 14px;
  font-family: inherit;
  resize: vertical;
  margin-bottom: 12px;

  &:focus {
    outline: none;
    border-color: #ff6b35;
    box-shadow: 0 0 0 2px rgba(255, 107, 53, 0.2);
  }
`;

const AnswerActions = styled.div`
  display: flex;
  gap: 8px;
  justify-content: flex-end;
`;

const NoQuestions = styled.div`
  text-align: center;
  padding: 40px 20px;
  color: #666;
  font-size: 16px;
`;

const LoadingSpinner = styled.div`
  text-align: center;
  padding: 40px 20px;
  color: #666;
`;

const ErrorMessage = styled.div`
  background: #f8d7da;
  color: #721c24;
  padding: 12px;
  border-radius: 6px;
  margin-bottom: 20px;
  font-size: 14px;
`;

const SuccessMessage = styled.div`
  background: #d4edda;
  color: #155724;
  padding: 12px;
  border-radius: 6px;
  margin-bottom: 20px;
  font-size: 14px;
`;

const ProductQuestions = ({ productId }) => {
  const { user, isAuthenticated: isAuth } = useAuth();
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [questionText, setQuestionText] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState(null);
  const [answeringQuestion, setAnsweringQuestion] = useState(null);
  const [answerText, setAnswerText] = useState('');

  // Загрузка вопросов
  const loadQuestions = useCallback(async () => {
    try {
      setLoading(true);
      const response = await apiRequest(`/products/${productId}/questions`);
      if (response.success) {
        setQuestions(response.data);
      }
    } catch (err) {
      setError('Ошибка при загрузке вопросов');
      console.error('Error loading questions:', err);
    } finally {
      setLoading(false);
    }
  }, [productId]);

  useEffect(() => {
    loadQuestions();
  }, [productId, loadQuestions]);

  // Отправка вопроса
  const handleSubmitQuestion = async () => {
    if (!questionText.trim()) {
      setError('Вопрос не может быть пустым');
      return;
    }

    try {
      setSubmitting(true);
      setError(null);
      
      const response = await apiRequest(`/products/${productId}/questions`, {
        method: 'POST',
        data: { question: questionText.trim() }
      });

      if (response.success) {
        setMessage('Вопрос успешно отправлен');
        setQuestionText('');
        setShowForm(false);
        loadQuestions(); // Перезагружаем вопросы
      } else {
        setError(response.message || 'Ошибка при отправке вопроса');
      }
    } catch (err) {
      setError('Ошибка при отправке вопроса');
      console.error('Error submitting question:', err);
    } finally {
      setSubmitting(false);
    }
  };

  // Ответ на вопрос (только для админов)
  const handleSubmitAnswer = async (questionId) => {
    if (!answerText.trim()) {
      setError('Ответ не может быть пустым');
      return;
    }

    try {
      setSubmitting(true);
      setError(null);
      
      const response = await apiRequest(`/admin/questions/${questionId}/answer`, {
        method: 'POST',
        data: { answer: answerText.trim() }
      });

      if (response.success) {
        setMessage('Ответ успешно добавлен');
        setAnswerText('');
        setAnsweringQuestion(null);
        loadQuestions(); // Перезагружаем вопросы
      } else {
        setError(response.message || 'Ошибка при добавлении ответа');
      }
    } catch (err) {
      setError('Ошибка при добавлении ответа');
      console.error('Error submitting answer:', err);
    } finally {
      setSubmitting(false);
    }
  };

  // Функция удаления вопроса убрана - удаление доступно только через админ панель

  // Форматирование даты
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('ru-RU', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Получение инициалов пользователя
  const getUserInitials = (name) => {
    if (!name) return '?';
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  if (loading) {
    return <LoadingSpinner>Загрузка вопросов...</LoadingSpinner>;
  }

  return (
    <QuestionsContainer>
      {error && <ErrorMessage>{error}</ErrorMessage>}
      {message && <SuccessMessage>{message}</SuccessMessage>}

      {/* Кнопка задать вопрос */}
      {isAuth() ? (
        <AskQuestionButton 
          onClick={() => setShowForm(true)}
          disabled={showForm}
        >
          <FaQuestion />
          ЗАДАТЬ ВОПРОС
        </AskQuestionButton>
      ) : (
        <AskQuestionButton disabled>
          Войдите, чтобы задать вопрос
        </AskQuestionButton>
      )}

      {/* Форма вопроса */}
      {showForm && (
        <QuestionForm>
          <FormTitle>Задать вопрос о товаре</FormTitle>
          <QuestionTextarea
            placeholder="Введите ваш вопрос..."
            value={questionText}
            onChange={(e) => setQuestionText(e.target.value)}
            maxLength={1000}
          />
          <FormActions>
            <CancelButton onClick={() => setShowForm(false)}>
              Отмена
            </CancelButton>
            <SubmitButton 
              onClick={handleSubmitQuestion}
              disabled={submitting || !questionText.trim()}
            >
              {submitting ? 'Отправка...' : 'Отправить вопрос'}
            </SubmitButton>
          </FormActions>
        </QuestionForm>
      )}

      {/* Список вопросов */}
      <QuestionsList>
        {questions.length === 0 ? (
          <NoQuestions>
            Пока нет вопросов о этом товаре. Будьте первым!
          </NoQuestions>
        ) : (
          questions.map((question) => (
            <QuestionItem key={question.id}>
              <QuestionHeader>
                <QuestionInfo>
                  <UserAvatar>
                    {getUserInitials(question.user_name || question.first_name)}
                  </UserAvatar>
                  <QuestionMeta>
                    <QuestionAuthor>
                      {question.user_name || `${question.first_name} ${question.last_name}`}
                    </QuestionAuthor>
                    <QuestionDate>
                      <FaClock />
                      {formatDate(question.created_at)}
                    </QuestionDate>
                  </QuestionMeta>
                </QuestionInfo>
                
                {/* Действия для админов - только ответ на сайте, удаление только через админ панель */}
                {user?.role === 'admin' && (
                  <QuestionActions>
                    {!question.answer && (
                      <ActionButton
                        onClick={() => setAnsweringQuestion(question.id)}
                        title="Ответить"
                      >
                        <FaReply />
                      </ActionButton>
                    )}
                    {/* Кнопка удаления убрана с сайта, доступна только в админ панели */}
                  </QuestionActions>
                )}
              </QuestionHeader>

              <QuestionText>{question.question}</QuestionText>

              {/* Ответ на вопрос */}
              {question.answer && (
                <AnswerSection>
                  <AnswerHeader>
                    <FaCheck style={{ color: '#28a745' }} />
                    <strong>Ответ администратора</strong>
                    {question.admin_name && question.admin_name.trim() && question.admin_name !== 'undefined undefined' && ` от ${question.admin_name}`}
                    {question.answered_at && (
                      <>
                        <FaClock />
                        {formatDate(question.answered_at)}
                      </>
                    )}
                  </AnswerHeader>
                  <AnswerText>{question.answer}</AnswerText>
                </AnswerSection>
              )}

              {/* Форма ответа для админов */}
              {user?.role === 'admin' && answeringQuestion === question.id && (
                <AnswerForm>
                  <AnswerTextarea
                    placeholder="Введите ответ на вопрос..."
                    value={answerText}
                    onChange={(e) => setAnswerText(e.target.value)}
                    maxLength={2000}
                  />
                  <AnswerActions>
                    <CancelButton onClick={() => setAnsweringQuestion(null)}>
                      Отмена
                    </CancelButton>
                    <SubmitButton 
                      onClick={() => handleSubmitAnswer(question.id)}
                      disabled={submitting || !answerText.trim()}
                    >
                      {submitting ? 'Отправка...' : 'Отправить ответ'}
                    </SubmitButton>
                  </AnswerActions>
                </AnswerForm>
              )}
            </QuestionItem>
          ))
        )}
      </QuestionsList>
    </QuestionsContainer>
  );
};

export default ProductQuestions; 